import { useEffect, useState } from "react";
import "./SelectedQuestionsPanel.css";

// The questions currently attached to a test, pinned above the picker.
//
// The browse list below is paginated server-side, so a test's questions are
// scattered across many pages and almost none of them appear on the page the
// picker opens on. That left an admin editing a test with no way to see what
// was already on it, let alone uncheck something. This section fetches the
// selection by id — independent of the browse list's paging, filters and
// search — so the full current selection is always visible and removable.

// Same approach as QuestionList's own stripHtml: question text is authored HTML
// and must be shown as plain text here, never rendered.
const stripHtml = (html) => {
  if (!html) return "—";
  const noTags = String(html).replace(/<[^>]*>/g, " ");
  const el = document.createElement("textarea");
  el.innerHTML = noTags;
  return el.value.replace(/\s+/g, " ").trim() || "—";
};

const SelectedQuestionsPanel = ({ selectedIds, onToggle }) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [collapsed, setCollapsed] = useState(false);

  // Depend on the contents, not the array identity — the parent rebuilds this
  // array on every render, which would otherwise refetch in a loop.
  const idsKey = (selectedIds || []).join(",");

  useEffect(() => {
    const ids = idsKey ? idsKey.split(",").filter(Boolean) : [];
    if (ids.length === 0) {
      setQuestions([]);
      setError("");
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        // pageSize covers the whole selection in one request; a test with more
        // questions than this would page, which no test currently approaches.
        const params = new URLSearchParams({ ids: ids.join(","), page: "1", pageSize: "500" });
        const res = await fetch(`${API_URL}/api/questions?${params.toString()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (cancelled) return;

        // Show them in the order they are selected in, not the order the API
        // returns, so a question just ticked appears where the admin expects.
        const byId = new Map((json.data || []).map((q) => [String(q._id), q]));
        setQuestions(ids.map((id) => byId.get(String(id))).filter(Boolean));
      } catch (e) {
        if (!cancelled) setError("Could not load the selected questions.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [idsKey, API_URL]);

  const count = (selectedIds || []).length;
  if (count === 0) {
    return (
      <div className="sqp">
        <div className="sqp-head">
          <h3 className="sqp-title">Selected questions <span className="sqp-count">0</span></h3>
        </div>
        <p className="sqp-empty">No questions selected yet. Tick questions below to add them to this test.</p>
      </div>
    );
  }

  return (
    <div className="sqp">
      <div className="sqp-head">
        <h3 className="sqp-title">
          Selected questions <span className="sqp-count">{count}</span>
        </h3>
        <button type="button" className="sqp-toggle" onClick={() => setCollapsed((c) => !c)}>
          {collapsed ? "Show" : "Hide"}
        </button>
      </div>

      {!collapsed && (
        <>
          {loading && <p className="sqp-note">Loading…</p>}
          {error && <p className="sqp-note sqp-error">{error}</p>}
          {!loading && !error && (
            <ul className="sqp-list">
              {questions.map((q) => (
                <li key={q._id} className="sqp-item">
                  <label className="sqp-label">
                    <input
                      type="checkbox"
                      checked
                      onChange={() => onToggle(q._id)}
                    />
                    <span className="sqp-text">{stripHtml(q.question)}</span>
                  </label>
                  <span className="sqp-meta">
                    {[q.subject, q.module, q.difficultyLevel].filter(Boolean).join(" · ")}
                  </span>
                </li>
              ))}
              {/* A selected id with no matching question means the question was
                  deleted from the bank after being attached to this test. It is
                  still on the test, so it is shown rather than quietly dropped. */}
              {questions.length < count && (
                <li className="sqp-item sqp-missing">
                  {count - questions.length} selected question
                  {count - questions.length === 1 ? "" : "s"} could not be loaded — they may have been deleted.
                </li>
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default SelectedQuestionsPanel;
